// One group per connectable Microsoft surface. The scope strings of each stay in the API
// (MICROSOFT_SCOPE_GROUPS); this is what the web names when it connects or disconnects one.
export enum MicrosoftScopeGroup {
  CALENDAR = 'calendar',
  MAIL = 'mail',
  TEAMS = 'teams',
}

export const OUTLOOK_SCOPE_GROUPS = [
  MicrosoftScopeGroup.CALENDAR,
  MicrosoftScopeGroup.MAIL,
] as const;

export type OutlookScopeGroup = (typeof OUTLOOK_SCOPE_GROUPS)[number];

export interface MicrosoftCredentialsItemKeys {
  userId: string; // Primary key
  homeAccountId: string; // Sort key
}

export interface MicrosoftCredentialsItemFields {
  // Accepted-key exception (2026-09 pre-SOC2 audit): HASH key of
  // MICROSOFT_CREDENTIALS_BY_OUTLOOK_ADDRESS_INDEX and range key (with userId)
  // of MICROSOFT_CREDENTIALS_BY_USER_AND_OUTLOOK_ADDRESS_INDEX
  // (`getMicrosoftCredentialsItemByOutlookAddress` / `getOutlookCredentialsItemByUserAndAddress`
  // in dynamo-db.ts) — both exact-match GSI lookups, so sealing it would turn
  // every one into a miss. Same accepted class as `HTUsers.Email`.
  outlookAddress?: string;
  outlookEventSubscriptionId?: string;
  outlookEventClientState?: string;
  outlookMessageSubscriptionId?: string;
  outlookMessageClientState?: string;
  teamsSubscriptionId?: string;
  teamsClientState?: string;
  outlookMessageRecoveryWatermark?: string;
  outlookMessageRecoveryPausedUntil?: string;
  shouldPreserveData?: boolean;
  // The dead-grant handler's claim per platform, so parallel records on one dead token send one notice.
  // Structural, not PII (encryption.md reason 2): the claim's condition reads it.
  outlookInvalidGrantClaimedAt?: number;
  teamsInvalidGrantClaimedAt?: number;
  // Stamped once by the connect that creates the row: the oldest holder owns a shared account.
  // Structural, not PII (encryption.md reason 2): resolveAccountOwner sorts on it.
  connectedAt?: number;
}

export interface MicrosoftCredentialsItem
  extends MicrosoftCredentialsItemKeys,
    MicrosoftCredentialsItemFields {}

// Coded so the web can show its own copy instead of a generic connect failure.
export enum MicrosoftConnectRefusal {
  // Another app user holds this Microsoft account; they would share one MSAL token partition.
  ACCOUNT_HELD_BY_ANOTHER_USER = 'account_held_by_another_user',
}

// Why a connect failed, coded so its response carries no provider or internal error text.
export enum MicrosoftConnectFailure {
  // None at all: a partial grant connects what it covers.
  PERMISSIONS_NOT_GRANTED = 'permissions_not_granted',
  // Graph has no Teams chat subscriptions for a personal account, only for work or school.
  TEAMS_PERSONAL_ACCOUNT = 'teams_personal_account',
  // A callback that cannot be redeemed as sent: an unparseable body, a bad or expired state, groups
  // foreign to the platform, or a code already used.
  INVALID_REQUEST = 'invalid_request',
  CONNECT_FAILED = 'connect_failed',
}

export interface CachedOutlookFolder {
  displayName: string;
  parentFolderId: string;
  wellKnownName?: string;
  isHidden: boolean;
}

export interface OutlookWellKnownFolderIds {
  inboxFolderId?: string;
  sentFolderId?: string;
  archiveFolderId?: string;
  spamFolderId?: string;
  trashFolderId?: string;
  draftsFolderId?: string;
}

export interface OutlookFolderCacheItem {
  userId: string;
  homeAccountId: string;
  // Accepted-key exception (2026-09 pre-SOC2 audit): same accepted class as
  // `MicrosoftCredentialsItemFields.outlookAddress` above — this row's copy of
  // the same account address, and it sits beside nothing else sensitive
  // (folder names/ids, sync cursors, TTL). Not itself a DynamoDB key on this
  // table (PK is userId, SK is homeAccountId), but left readable alongside its
  // source-of-truth rather than sealed independently.
  outlookAddress: string;
  folders: Record<string, CachedOutlookFolder>;
  wellKnownFolderIds?: OutlookWellKnownFolderIds;
  syncedAt: number; // 0 = bootstrap item, not yet synced — treat as absent for reads
  rev: number;
  deltaLink?: string;
  truncated?: boolean;
  ttl?: number; // legacy only; no longer written
}

export interface MicrosoftIntegrationStatus {
  userId: string;
  homeAccountId: string;
  outlookAddress: string;
  outlookEnabled: boolean;
  calendarEnabled: boolean;
  teamsEnabled: boolean;
}
